import React, { useEffect, useMemo, useState } from "react";
import "./InstallPWA.css";

const InstallPWA = () => {
  const [showModal, setShowModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const appName = useMemo(() => {
    const portalTitle = localStorage.getItem("tituloWebPortal") || "Portal Pacientes";
    const clinicName = localStorage.getItem("nombreClinica") || "Clínica San José";
    return `${portalTitle} ${clinicName}`.trim();
  }, []);

  const iosDismissKey = useMemo(() => {
    const clinicName = localStorage.getItem("nombreClinica") || "ClinicaSanJose";
    return `iosModalDismissed${clinicName.replace(/[^a-zA-Z0-9]/g, "")}`;
  }, []);

  useEffect(() => {
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isAndroidDevice = /Android/i.test(navigator.userAgent);
    const standaloneMode =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;

    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);
    setIsStandalone(standaloneMode);

    if (standaloneMode) {
      return undefined;
    }

    if (isIOSDevice) {
      const hasSeenIOSInstructions = localStorage.getItem(iosDismissKey);
      if (!hasSeenIOSInstructions) {
        const timer = setTimeout(() => setShowModal(true), 2000);
        return () => clearTimeout(timer);
      }
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShowModal(true);
    };

    const handleInstalled = () => {
      setDeferredPrompt(null);
      setShowModal(false);
      setIsStandalone(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    const fallbackTimer = setTimeout(() => {
      setShowModal((currentValue) => {
        if (currentValue || standaloneMode || isIOSDevice) {
          return currentValue;
        }
        return true;
      });
    }, 2000);

    return () => {
      clearTimeout(fallbackTimer);
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, [iosDismissKey]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setDeferredPrompt(null);
    }
    setShowModal(false);
  };

  const handleModalClose = () => {
    setShowModal(false);
    if (isIOS) {
      localStorage.setItem(iosDismissKey, "true");
    }
  };

  if (!showModal || isStandalone) return null;

  const isChromeLike = /Chrome|CriOS|EdgA/i.test(navigator.userAgent);
  const showManualAndroidInstructions = isAndroid && !deferredPrompt;
  const showInstallButton = !isIOS && deferredPrompt;
  const useGenericDesktopCopy = !isIOS && !isAndroid;

  return (
    <div className="pwa-modal-overlay">
      <div className="pwa-modal">
        <h2>
          {"Crear acceso directo"}
        </h2>
        {isIOS ? (
          <div className="ios-instructions">
            <p>
              Creá el acceso directo de {appName} para entrar más rápido desde tu
              dispositivo.
            </p>
            <ol>
              <li>
                Toca el botón Compartir en Safari o Chrome para iPhone.
              </li>
              <li>
                Elegí "Agregar a pantalla de inicio"{" "}
                <span className="ios-icon">+</span>.
              </li>
              <li>Tocá "Agregar" para confirmar.</li>
            </ol>
            <p>iOS no muestra un botón nativo para crear este acceso directo desde la web.</p>
          </div>
        ) : showManualAndroidInstructions ? (
          <div className="ios-instructions">
            <p>
              Creá el acceso directo de {appName} para entrar más rápido desde tu
              dispositivo.
            </p>
            <ol>
              <li>Tocá el menú de tres puntos del navegador.</li>
              <li>Elegí "Instalar app" o "Agregar a pantalla principal".</li>
              <li>Confirmá la creación del acceso directo.</li>
            </ol>
          </div>
        ) : useGenericDesktopCopy ? (
          <p>
            Creá el acceso directo de {appName} para entrar más rápido desde tu
            dispositivo.
          </p>
        ) : (
          <p>
            Creá el acceso directo de {appName} para entrar más rápido desde tu dispositivo.
          </p>
        )}
        <div className="pwa-modal-buttons">
          {showInstallButton && (
            <button onClick={handleInstall} className="install-button">
              Instalar
            </button>
          )}
          <button onClick={handleModalClose} className="close-button">
            {isIOS ? "Entendido" : "Ahora no"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
